import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, RefreshCw, PackagePlus, X, ExternalLink } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'medicion', name: 'Medición y Marcado' },
  { id: 'corte', name: 'Corte' },
  { id: 'fijacion', name: 'Fijación' },
  { id: 'acabado', name: 'Acabado y Pasta' },
  { id: 'lijado', name: 'Lijado' },
  { id: 'electricas', name: 'Herramientas Eléctricas' },
  { id: 'seguridad', name: 'Seguridad (EPP)' },
  { id: 'andamios', name: 'Andamios y Acceso' },
  { id: 'kits', name: 'Kits y Packs' },
];

const EMPTY = {
  name: '', description: '', category: 'kits',
  price_usd: '', price_eur: '', cost_usd: '',
  supplier_name: '', supplier_url: '', supplier_sku: '', supplier_email: '',
  image: '', stock: true, active: true,
};

const catName = (id) => CATEGORIES.find((c) => c.id === id)?.name || id;

export default function CustomProductsPanel() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/custom-products`);
      setProducts(r.data || []);
    } catch (e) {
      toast.error('Error cargando productos: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setForm(EMPTY); setEditingId(null); setShowForm(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name || '', description: p.description || '', category: p.category || 'kits',
      price_usd: p.price_usd ?? '', price_eur: p.price_eur ?? '', cost_usd: p.cost_usd ?? '',
      supplier_name: p.supplier_name || '', supplier_url: p.supplier_url || '', supplier_sku: p.supplier_sku || '', supplier_email: p.supplier_email || '',
      image: p.image || '', stock: p.stock !== false, active: p.active !== false,
    });
    setEditingId(p.id);
    setShowForm(true);
  };

  const onUsdChange = (v) => {
    setForm((f) => ({ ...f, price_usd: v, price_eur: f.price_eur === '' && v !== '' ? (Math.round(parseFloat(v) * 0.9 * 100) / 100).toString() : f.price_eur }));
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (form.price_usd === '' || form.price_eur === '') { toast.error('Indica precio USD y EUR'); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description,
      category: form.category,
      price_usd: parseFloat(form.price_usd),
      price_eur: parseFloat(form.price_eur),
      cost_usd: form.cost_usd === '' ? 0 : parseFloat(form.cost_usd),
      supplier_name: form.supplier_name,
      supplier_url: form.supplier_url,
      supplier_sku: form.supplier_sku,
      supplier_email: form.supplier_email,
      image: form.image,
      stock: !!form.stock,
      active: !!form.active,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/custom-products/${editingId}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/custom-products`, payload);
        toast.success('Producto creado');
      }
      setShowForm(false);
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error('Error al guardar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${API}/custom-products/${p.id}`);
      toast.success('Producto eliminado');
      load();
    } catch (e) {
      toast.error('Error al eliminar: ' + (e.response?.data?.detail || e.message));
    }
  };

  const margin = (p) => {
    const m = (parseFloat(p.price_usd) || 0) - (parseFloat(p.cost_usd) || 0);
    return m;
  };

  return (
    <div className="space-y-6" data-testid="custom-products-panel">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-slate-800">
              <PackagePlus size={20} className="text-emerald-600" />
              Productos Manuales (Proveedores)
            </CardTitle>
            <p className="text-sm text-slate-500 mt-1">
              Productos de distribuidores especializados (fuera de CJ). Aparecen en la tienda y se gestionan manualmente.
            </p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="custom-refresh-btn" onClick={load} variant="outline" disabled={loading}>
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </Button>
            <Button data-testid="custom-add-btn" onClick={openCreate} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Plus size={16} className="mr-1" /> Añadir producto
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Form */}
          {showForm && (
            <div className="mb-6 p-4 border-2 border-emerald-200 rounded-lg bg-emerald-50/40" data-testid="custom-form">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-slate-800">{editingId ? 'Editar producto' : 'Nuevo producto'}</h4>
                <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700" data-testid="custom-form-close">
                  <X size={18} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Nombre *</Label>
                  <Input data-testid="custom-name" value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Ej: Llana inox Marshalltown 28cm" />
                </div>
                <div>
                  <Label>Categoría</Label>
                  <select
                    data-testid="custom-category"
                    value={form.category}
                    onChange={(e) => setField('category', e.target.value)}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
                  >
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Imagen (URL)</Label>
                  <Input data-testid="custom-image" value={form.image} onChange={(e) => setField('image', e.target.value)} placeholder="https://..." />
                </div>
                <div className="md:col-span-2">
                  <Label>Descripción</Label>
                  <Textarea data-testid="custom-description" value={form.description} onChange={(e) => setField('description', e.target.value)} rows={2} placeholder="Descripción del producto..." />
                </div>
                <div>
                  <Label>Precio venta USD *</Label>
                  <Input data-testid="custom-price-usd" type="number" step="0.01" value={form.price_usd} onChange={(e) => onUsdChange(e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <Label>Precio venta EUR *</Label>
                  <Input data-testid="custom-price-eur" type="number" step="0.01" value={form.price_eur} onChange={(e) => setField('price_eur', e.target.value)} placeholder="0.00" />
                </div>
                <div>
                  <Label>Coste (lo que pagas) USD</Label>
                  <Input data-testid="custom-cost-usd" type="number" step="0.01" value={form.cost_usd} onChange={(e) => setField('cost_usd', e.target.value)} placeholder="0.00" />
                </div>
                <div className="flex items-end">
                  {form.price_usd !== '' && form.cost_usd !== '' && (
                    <div className="text-sm text-slate-600">
                      Margen bruto: <span className="font-semibold text-emerald-700">${(parseFloat(form.price_usd || 0) - parseFloat(form.cost_usd || 0)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Proveedor</Label>
                  <Input data-testid="custom-supplier-name" value={form.supplier_name} onChange={(e) => setField('supplier_name', e.target.value)} placeholder="Ej: Marshalltown / Rubi" />
                </div>
                <div>
                  <Label>SKU proveedor</Label>
                  <Input data-testid="custom-supplier-sku" value={form.supplier_sku} onChange={(e) => setField('supplier_sku', e.target.value)} placeholder="Código del proveedor" />
                </div>
                <div>
                  <Label>Email del proveedor</Label>
                  <Input data-testid="custom-supplier-email" type="email" value={form.supplier_email} onChange={(e) => setField('supplier_email', e.target.value)} placeholder="pedidos@proveedor.com" />
                </div>
                <div className="md:col-span-2">
                  <Label>URL del producto en el proveedor</Label>
                  <Input data-testid="custom-supplier-url" value={form.supplier_url} onChange={(e) => setField('supplier_url', e.target.value)} placeholder="https://proveedor.com/producto" />
                </div>
                <div className="flex items-center gap-4 md:col-span-2">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="custom-stock" checked={form.stock} onChange={(e) => setField('stock', e.target.checked)} /> En stock
                  </label>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" data-testid="custom-active" checked={form.active} onChange={(e) => setField('active', e.target.checked)} /> Activo (visible en tienda)
                  </label>
                </div>
              </div>
              {form.image && (
                <img src={form.image} alt="preview" className="mt-3 h-24 w-24 object-cover rounded border" onError={(e) => { e.target.style.display = 'none'; }} />
              )}
              <div className="flex gap-2 mt-4">
                <Button data-testid="custom-save-btn" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  {saving ? <RefreshCw size={16} className="animate-spin mr-1" /> : null}
                  {editingId ? 'Guardar cambios' : 'Crear producto'}
                </Button>
                <Button onClick={() => setShowForm(false)} variant="outline">Cancelar</Button>
              </div>
            </div>
          )}

          {/* List */}
          {products.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="custom-empty">
              No hay productos manuales todavía. Pulsa "Añadir producto" para crear el primero.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="custom-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3">Producto</th>
                    <th className="py-2 px-2">Categoría</th>
                    <th className="py-2 px-2">Venta</th>
                    <th className="py-2 px-2">Coste</th>
                    <th className="py-2 px-2">Margen</th>
                    <th className="py-2 px-2">Proveedor</th>
                    <th className="py-2 px-2">Estado</th>
                    <th className="py-2 pl-2 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`custom-row-${p.id}`}>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-2">
                          {p.image ? <img src={p.image} alt="" className="h-9 w-9 rounded object-cover border" onError={(e) => { e.target.style.visibility = 'hidden'; }} /> : <div className="h-9 w-9 rounded bg-slate-100" />}
                          <div>
                            <div className="font-medium text-slate-800">{p.name}</div>
                            <div className="text-xs text-slate-400">{p.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-slate-600">{catName(p.category)}</td>
                      <td className="py-2 px-2 text-slate-800">${Number(p.price_usd).toFixed(2)}</td>
                      <td className="py-2 px-2 text-slate-600">${Number(p.cost_usd || 0).toFixed(2)}</td>
                      <td className="py-2 px-2 font-semibold text-emerald-700">${margin(p).toFixed(2)}</td>
                      <td className="py-2 px-2 text-slate-600">
                        {p.supplier_url ? (
                          <a href={p.supplier_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                            {p.supplier_name || 'Ver'} <ExternalLink size={12} />
                          </a>
                        ) : (p.supplier_name || '—')}
                      </td>
                      <td className="py-2 px-2">
                        {p.active ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Activo</Badge> : <Badge variant="outline" className="text-slate-400">Oculto</Badge>}
                        {!p.stock && <Badge variant="outline" className="ml-1 text-red-500 border-red-200">Sin stock</Badge>}
                      </td>
                      <td className="py-2 pl-2">
                        <div className="flex gap-1 justify-end">
                          <Button data-testid={`custom-edit-${p.id}`} onClick={() => openEdit(p)} size="sm" variant="outline" className="h-8 w-8 p-0">
                            <Pencil size={14} />
                          </Button>
                          <Button data-testid={`custom-delete-${p.id}`} onClick={() => remove(p)} size="sm" variant="outline" className="h-8 w-8 p-0 text-red-600 hover:bg-red-50">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
